import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Alert, 
  Badge
} from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import * as Icons from 'react-icons/fa';
import { contactsAPI } from '../services/api';
import Icon from '../components/Icon';

interface Contact {
  _id: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const ContactDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch contact details on component mount
  useEffect(() => {
    if (!id) return;
    
    const fetchContactDetails = async () => {
      setLoading(true);
      try {
        const response = await contactsAPI.getContactById(id);
        setContact(response.data);
        
        // Mark as read if not already
        if (!response.data.isRead) {
          await contactsAPI.markContactAsRead(id);
        }
      } catch (err: any) {
        setError('Failed to fetch contact details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchContactDetails();
  }, [id]);

  // Handle contact deletion
  const handleDelete = async () => {
    if (!id) return;
    
    try {
      await contactsAPI.deleteContact(id);
      navigate('/contacts');
    } catch (err: any) {
      setError('Failed to delete contact');
      console.error(err);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading contact details...</p>
        </div>
      </Container>
    );
  }

  if (error || !contact) {
    return (
      <Container>
        <Alert variant="danger" className="my-5">
          {error || 'Contact not found'}
        </Alert>
        <Link to="/contacts" className="btn btn-primary">
          <Icon icon={Icons.FaArrowLeft} className="me-2" /> Back to Contacts
        </Link>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">Contact Details</h1>
        </Col>
        <Col xs="auto">
          <Link to="/contacts">
            <Button variant="outline-secondary">
              <Icon icon={Icons.FaArrowLeft} className="me-2" /> Back to Contacts
            </Button>
          </Link>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{contact.subject}</h5>
          <Badge bg={contact.isRead ? 'success' : 'danger'}>
            {contact.isRead ? 'Read' : 'Unread'}
          </Badge>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <div className="mb-3">
                <h6 className="text-muted mb-2">Sender Information</h6>
                <p className="mb-2">
                  <strong>Name:</strong> {contact.name}
                </p>
                <p className="mb-2">
                  <Icon icon={Icons.FaEnvelope} className="me-2 text-primary" />
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </p>
                {contact.phone && (
                  <p className="mb-0">
                    <Icon icon={Icons.FaPhone} className="me-2 text-primary" />
                    <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                  </p>
                )}
              </div>
            </Col>
            <Col md={6}>
              <h6 className="text-muted mb-2">Request Details</h6>
              <p className="mb-2">
                <Icon icon={Icons.FaCalendar} className="me-2 text-primary" />
                {formatDate(contact.createdAt)}
              </p>
              {contact.isRead && (
                <p className="mb-0">
                  <Icon icon={Icons.FaCheckCircle} className="me-2 text-success" />
                  Marked as read
                </p>
              )}
            </Col>
          </Row>

          <div className="mb-4">
            <h5 className="mb-3">Message</h5>
            <div className="p-3 bg-light rounded">
              <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{contact.message}</p>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-4">
            <Button variant="outline-primary" as="a" href={`mailto:${contact.email}`}>
              <Icon icon={Icons.FaEnvelope} className="me-2" /> Reply via Email
            </Button>
            {showDeleteConfirm ? (
              <div>
                <span className="me-2">Are you sure?</span>
                <Button variant="danger" size="sm" onClick={handleDelete} className="me-2">
                  Yes, Delete
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="outline-danger" onClick={() => setShowDeleteConfirm(true)}>
                <Icon icon={Icons.FaTrash} className="me-2" /> Delete Contact
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ContactDetails; 